import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CLIENT_INFO_STEP_TITLE } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/client-info/constants.ts';
import type {
    ClientTenantDetails,
    ClientTenantTypesResponse,
    CreateClientTenantRequest,
    CreateClientTenantResponse,
    TenantLogoResponse,
    TenantType,
    UpdateClientTenantRequest,
    UpdateClientTenantResponse,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/tenant-info/api/tenantInfo.api.ts';
import { createOrUpdateApiClientTenantLogoWithUploadProgress } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/tenant-info/api/tenantInfo.api.ts';
import {
    createApiClientTenantWithLogo,
    deleteApiClientTenant,
    getApiClientTenantByAlias,
    getApiClientTenantTypes,
    updateApiClientTenant,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/tenant-info/api/tenantInfo.api.ts';
import { TENANT_INFO_STEP_TITLE } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/tenant-info/constants.ts';
import { setPublishedSteps } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/model/configuration.slice.ts';
import { extractError } from '@utils/slice.ts';

export interface TenantInfoError {
    message?: string;
    [k: string]: any;
}

export interface TenantInfoState {
    error: TenantInfoError | null;

    createdTenantInfo: CreateClientTenantResponse | null;
    tenantInfo: ClientTenantDetails | null;
    tempCreatedTenantLogoData: TenantLogoResponse | null;

    tenantTypes: TenantType[];
    tenantHost: string;
    tenantAlias: string;
    tenantLogoURl: string;
    isChangeTenantLogoModalOpened: boolean;

    // flags
    isCreateTenantInfoPending: boolean;
    isCreateTenantInfoWithImagePending: boolean;
    isGetTenantInfoPending: boolean;
    isUpdateTenantInfoPending: boolean;
    isUploadTenantInfoLogoPending: boolean;
    isUploadTenantInfoTempLogoPending: boolean;
    isDeleteTenantInfoPending: boolean;
    isGetTenantTypesPending: boolean;
}

const initialState: TenantInfoState = {
    error: null,

    createdTenantInfo: null,
    tenantInfo: null,
    tempCreatedTenantLogoData: null,

    tenantTypes: [],
    tenantHost: '',
    tenantAlias: '',
    tenantLogoURl: '',

    isChangeTenantLogoModalOpened: false,
    isCreateTenantInfoPending: false,
    isCreateTenantInfoWithImagePending: false,
    isGetTenantInfoPending: false,
    isUpdateTenantInfoPending: false,
    isUploadTenantInfoLogoPending: false,
    isUploadTenantInfoTempLogoPending: false,
    isDeleteTenantInfoPending: false,
    isGetTenantTypesPending: false,
};

export const createTenantInfoWithImage = createAsyncThunk<
    CreateClientTenantResponse,
    { clientId: number; payload: CreateClientTenantRequest },
    { rejectValue: TenantInfoError }
>('adminCenterTenantInfo/createTenantWithImage', ({ clientId, payload }, { rejectWithValue, dispatch }) =>
    createApiClientTenantWithLogo(clientId, payload)
        .then((res) => {
            dispatch(setPublishedSteps(CLIENT_INFO_STEP_TITLE));
            dispatch(setPublishedSteps(TENANT_INFO_STEP_TITLE));

            return res;
        })
        .catch((err) => rejectWithValue(err)),
);

export const createOrUpdateTenantInfoLogoWithUploadProgress = createAsyncThunk<
    any,
    {
        clientId: number;
        formData: FormData;
        onUploadProgress?: (progress: number) => void;
        signal?: AbortSignal;
    },
    { rejectValue: TenantInfoError }
>(
    'adminCenterTenantInfo/createOrUpdateTenantInfoLogo',
    ({ clientId, formData, onUploadProgress, signal }, { rejectWithValue }) =>
        createOrUpdateApiClientTenantLogoWithUploadProgress(clientId, formData, onUploadProgress, signal)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

export const getTenantInfoByAlias = createAsyncThunk<
    ClientTenantDetails,
    { clientId: number; alias: string },
    { rejectValue: TenantInfoError }
>('adminCenterTenantInfo/getTenantByAlias', ({ clientId, alias }, { rejectWithValue }) =>
    getApiClientTenantByAlias(clientId, alias)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateTenantInfo = createAsyncThunk<
    UpdateClientTenantResponse,
    { clientId: number; alias: string; payload: UpdateClientTenantRequest },
    { rejectValue: TenantInfoError }
>('adminCenterTenantInfo/updateTenant', ({ clientId, alias, payload }, { rejectWithValue }) =>
    updateApiClientTenant(clientId, alias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteTenantInfo = createAsyncThunk<
    any,
    { clientId: number; alias: string; deleteTenantDatabase?: boolean },
    { rejectValue: TenantInfoError }
>('adminCenterTenantInfo/deleteTenant', ({ clientId, alias, deleteTenantDatabase = false }, { rejectWithValue }) =>
    deleteApiClientTenant(clientId, alias, deleteTenantDatabase)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getTenantInfoTypes = createAsyncThunk<ClientTenantTypesResponse, number, { rejectValue: TenantInfoError }>(
    'adminCenterTenantInfo/getTenantTypes',
    (clientId, { rejectWithValue }) =>
        getApiClientTenantTypes(clientId)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
);

const tenantInfoSlice = createSlice({
    name: 'adminCenterTenantInfo',
    initialState,
    reducers: {
        clearTenantInfoState(state) {
            state.error = null;
            state.createdTenantInfo = null;
            state.tenantInfo = null;
            state.tenantTypes = [];
            state.tenantHost = '';
            state.tenantAlias = '';
            state.tempCreatedTenantLogoData = null;

            state.isCreateTenantInfoPending = false;
            state.isCreateTenantInfoWithImagePending = false;
            state.isGetTenantInfoPending = false;
            state.isUpdateTenantInfoPending = false;
            state.isUploadTenantInfoLogoPending = false;
            state.isDeleteTenantInfoPending = false;
            state.isGetTenantTypesPending = false;
        },
        setChangeTenantLogoModalOpened: (state, action: PayloadAction<boolean>) => {
            state.isChangeTenantLogoModalOpened = action.payload;
        },
        setTenantEditContext(state, action: PayloadAction<{ alias: string }>) {
            const { alias } = action.payload;
            state.tenantAlias = alias ?? '';
        },
        clearTempCreatedTenantLogoData: (state) => {
            state.tempCreatedTenantLogoData = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(createTenantInfoWithImage.pending, (state) => {
            state.isCreateTenantInfoWithImagePending = true;
            state.error = null;
        });
        builder.addCase(createTenantInfoWithImage.fulfilled, (state, action) => {
            state.isCreateTenantInfoWithImagePending = false;
            state.tenantLogoURl = action.payload.url;
            state.createdTenantInfo = action.payload;
            state.tenantAlias = action.payload.alias;
        });
        builder.addCase(createTenantInfoWithImage.rejected, (state, action) => {
            state.isCreateTenantInfoWithImagePending = false;
            state.error = extractError(action);
        });

        builder.addCase(createOrUpdateTenantInfoLogoWithUploadProgress.pending, (state) => {
            state.isUploadTenantInfoTempLogoPending = true;
            state.error = null;
            state.tempCreatedTenantLogoData = null;
        });
        builder.addCase(createOrUpdateTenantInfoLogoWithUploadProgress.fulfilled, (state, action) => {
            state.isUploadTenantInfoTempLogoPending = false;
            state.tempCreatedTenantLogoData = action.payload;
        });
        builder.addCase(createOrUpdateTenantInfoLogoWithUploadProgress.rejected, (state, action) => {
            state.isUploadTenantInfoTempLogoPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getTenantInfoByAlias.pending, (state) => {
            state.isGetTenantInfoPending = true;
            state.error = null;
        });
        builder.addCase(getTenantInfoByAlias.fulfilled, (state, action) => {
            state.isGetTenantInfoPending = false;
            state.tenantInfo = action.payload;
        });
        builder.addCase(getTenantInfoByAlias.rejected, (state, action) => {
            state.isGetTenantInfoPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateTenantInfo.pending, (state) => {
            state.isUpdateTenantInfoPending = true;
            state.error = null;
        });
        builder.addCase(updateTenantInfo.fulfilled, (state, action) => {
            state.isUpdateTenantInfoPending = false;
            state.tenantInfo = action.payload;
            state.tenantAlias = action.payload.alias;
            state.tenantLogoURl = action.payload.url;
        });
        builder.addCase(updateTenantInfo.rejected, (state, action) => {
            state.isUpdateTenantInfoPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteTenantInfo.pending, (state) => {
            state.isDeleteTenantInfoPending = true;
            state.error = null;
        });
        builder.addCase(deleteTenantInfo.fulfilled, (state, action) => {
            state.isDeleteTenantInfoPending = false;
            const deletedAlias = action.meta.arg.alias;
            if (state.tenantInfo?.alias === deletedAlias) {
                state.tenantInfo = null;
            }
        });
        builder.addCase(deleteTenantInfo.rejected, (state, action) => {
            state.isDeleteTenantInfoPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getTenantInfoTypes.pending, (state) => {
            state.isGetTenantTypesPending = true;
            state.error = null;
        });
        builder.addCase(getTenantInfoTypes.fulfilled, (state, action) => {
            state.isGetTenantTypesPending = false;
            state.tenantTypes = action.payload.tenantTypes ?? [];
            state.tenantHost = action.payload.host ?? '';
        });
        builder.addCase(getTenantInfoTypes.rejected, (state, action) => {
            state.isGetTenantTypesPending = false;
            state.error =
                action.payload && typeof action.payload === 'object'
                    ? action.payload
                    : { message: action.error.message };
        });
    },
});

export const {
    clearTenantInfoState,
    setChangeTenantLogoModalOpened,
    setTenantEditContext,
    clearTempCreatedTenantLogoData,
} = tenantInfoSlice.actions;
export default tenantInfoSlice.reducer;
